import {
  FETCH_BOOKING_DISCOUNT,
  FETCH_BOOKING__DISCOUNT_SUCCESS,
  FETCH_BOOKING__DISCOUNT_FAILED,
} from "../store/types";
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { get } from "firebase/database";

// Portal-only earnings rollup (settings.useReportRollups) — reads the
// CF-maintained /report_rollups/system_monthly node (a few KB) instead of the
// entire /bookings tree. Mirrors the useCustomerOnDemand gate in
// usersactions.js: an absent/false flag, or any role outside the staff set
// (fleetadmin and every mobile role included), keeps the legacy full scan
// below byte-identical.
const ROLLUP_ROLES = ['admin', 'finance', 'callcenteragent', 'callcentersupervisor'];
const useReportRollups = () => {
  try { return store.getState().settingsdata.settings.useReportRollups === true; } catch (_) { return false; }
};
const rollupEligible = () => {
  try { return ROLLUP_ROLES.indexOf(store.getState().auth.profile.usertype) >= 0; } catch (_) { return false; }
};
const MONTH_KEY_RE = /^\d{4}-\d{2}$/;
const ADDIS_OFFSET_MS = 3 * 60 * 60 * 1000;
const MONTHS_NAME = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
// Rollup month keys are Addis-time 'YYYY-MM'; `dated` is the first instant of
// that month (legacy used the first counted booking's tripdate — no view
// renders it, but the field is kept so the payload shape is unchanged).
const parseMonthKey = (mk) => {
  const year = parseInt(mk.slice(0, 4), 10);
  const month = parseInt(mk.slice(5, 7), 10) - 1;
  return { year: year, month: month, dated: Date.UTC(year, month, 1) - ADDIS_OFFSET_MS };
};
// Map one system_monthly record to the exact row the legacy branch builds:
// money fields as toFixed(settings.decimal) strings, the derived myEarning /
// customerPaid(+cancellationFee) / rideCost the same way legacy derives them.
const mapSystemMonth = (mk, r, settings) => {
  const { year, month, dated } = parseMonthKey(mk);
  const customerPaid = num(r.customerPaid);
  const cancellationFee = num(r.cancellationFee);
  const driverShare = num(r.driverShare);
  const tripCost = num(r.tripCost);
  const convenienceFee = num(r.convenienceFee);
  return {
    dated: dated,
    year: year,
    month: month,
    monthsName: MONTHS_NAME[month],
    uniqueKey: year + '_' + month,
    total_rides: num(r.total_rides),
    discountAmount: num(r.discountAmount).toFixed(settings.decimal),
    driverShare: driverShare.toFixed(settings.decimal),
    customerPaid: (customerPaid + cancellationFee).toFixed(settings.decimal),
    convenienceFee: convenienceFee.toFixed(settings.decimal),
    tripCost: tripCost.toFixed(settings.decimal),
    cancellationFee: cancellationFee.toFixed(settings.decimal),
    fleetadminFee: num(r.fleetadminFee).toFixed(settings.decimal),
    myEarning: (customerPaid + cancellationFee - driverShare).toFixed(settings.decimal),
    rideCost: (tripCost - convenienceFee).toFixed(settings.decimal)
  };
};

export const fetchEarningsReport = () => async (dispatch) => {

  const {
    bookingRef,
    settingsRef,
    reportRollupSystemRef
  } = firebase;

  dispatch({
    type: FETCH_BOOKING_DISCOUNT,
    payload: null
  });

  const settingsdata = await get(settingsRef);
  const settings = settingsdata.val();

  if (useReportRollups() && rollupEligible()) {
    try {
      const rollupSnap = await get(reportRollupSystemRef);
      const months = rollupSnap.val();
      const arr = months
        ? Object.keys(months)
          .filter(mk => MONTH_KEY_RE.test(mk) && months[mk] && num(months[mk].total_rides) > 0)
          .map(mk => mapSystemMonth(mk, months[mk], settings))
        : [];
      if (arr.length) {
        dispatch({
          type: FETCH_BOOKING__DISCOUNT_SUCCESS,
          payload: arr
        });
      } else {
        dispatch({
          type: FETCH_BOOKING__DISCOUNT_FAILED,
          payload: "No data available."
        });
      }
    } catch (error) {
      dispatch({
        type: FETCH_BOOKING__DISCOUNT_FAILED,
        payload: error.message
      });
    }
    return;
  }

  const snapshot = await get(bookingRef);
  if (snapshot.val()) {
    const mainArr = snapshot.val();
    var monthsName = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    var renderobj = {};
    Object.keys(mainArr).map(j => {
      const status = mainArr[j].status;
      if (status === 'PAID' || status === 'COMPLETE' || (mainArr[j].status === 'CANCELLED' && mainArr[j].hasOwnProperty("cancellationFee"))) {
        let bdt = new Date(mainArr[j].tripdate);
        let uniqueKey = bdt.getFullYear() + '_' + bdt.getMonth();
        if (renderobj[uniqueKey]) {
          if(status == 'CANCELLED'){
            if(renderobj[uniqueKey].hasOwnProperty("cancellationFee")){
              renderobj[uniqueKey].cancellationFee = (parseFloat(renderobj[uniqueKey].cancellationFee) + parseFloat(mainArr[j].cancellationFee)).toFixed(settings.decimal);
            }else{
              renderobj[uniqueKey].cancellationFee = (parseFloat(mainArr[j].cancellationFee)).toFixed(settings.decimal);
            }
          }else{
            renderobj[uniqueKey].discountAmount = (parseFloat(renderobj[uniqueKey].discountAmount) + parseFloat(mainArr[j].discount)).toFixed(settings.decimal);
            renderobj[uniqueKey].driverShare = (parseFloat(renderobj[uniqueKey].driverShare) + parseFloat(mainArr[j].driver_share)).toFixed(settings.decimal);
            renderobj[uniqueKey].customerPaid = (parseFloat(renderobj[uniqueKey].customerPaid) + parseFloat(mainArr[j].customer_paid? mainArr[j].customer_paid: "0")).toFixed(settings.decimal);
            renderobj[uniqueKey].convenienceFee = (parseFloat(renderobj[uniqueKey].convenienceFee) + parseFloat(mainArr[j].convenience_fees)).toFixed(settings.decimal);
            renderobj[uniqueKey].tripCost = (parseFloat(renderobj[uniqueKey].tripCost) + parseFloat(mainArr[j].trip_cost)).toFixed(settings.decimal);
            if(mainArr[j].hasOwnProperty("fleetCommission")){
              renderobj[uniqueKey].fleetadminFee = (parseFloat(renderobj[uniqueKey].fleetadminFee) + parseFloat(mainArr[j].fleetCommission)).toFixed(settings.decimal);
            }
          }
          renderobj[uniqueKey]['total_rides'] = renderobj[uniqueKey]['total_rides'] + 1;
        } else {
          renderobj[uniqueKey] = {};
          renderobj[uniqueKey]['dated'] = mainArr[j].tripdate;
          renderobj[uniqueKey]['year'] = bdt.getFullYear();
          renderobj[uniqueKey]['month'] = bdt.getMonth();
          renderobj[uniqueKey]['monthsName'] = monthsName[bdt.getMonth()];
          renderobj[uniqueKey]['uniqueKey'] = uniqueKey;
          renderobj[uniqueKey]['total_rides'] = 1;
          if(status == 'CANCELLED'){
            renderobj[uniqueKey]['discountAmount'] = (0).toFixed(settings.decimal);
            renderobj[uniqueKey]['driverShare'] = (0).toFixed(settings.decimal);
            renderobj[uniqueKey]['customerPaid'] = (0).toFixed(settings.decimal);
            renderobj[uniqueKey]['convenienceFee'] = (0).toFixed(settings.decimal);
            renderobj[uniqueKey]['tripCost'] = (0).toFixed(settings.decimal);
            renderobj[uniqueKey]['cancellationFee'] = parseFloat(mainArr[j].cancellationFee).toFixed(settings.decimal);
          }else{
            renderobj[uniqueKey]['discountAmount'] = parseFloat(mainArr[j].discount).toFixed(settings.decimal);
            renderobj[uniqueKey]['driverShare'] = parseFloat(mainArr[j].driver_share).toFixed(settings.decimal);
            renderobj[uniqueKey]['customerPaid'] = parseFloat(mainArr[j].customer_paid? mainArr[j].customer_paid: "0").toFixed(settings.decimal);
            renderobj[uniqueKey]['convenienceFee'] = parseFloat(mainArr[j].convenience_fees).toFixed(settings.decimal);
            renderobj[uniqueKey]['tripCost'] = parseFloat(mainArr[j].trip_cost).toFixed(settings.decimal);
            renderobj[uniqueKey]['cancellationFee'] = (0).toFixed(settings.decimal);
            if(mainArr[j].fleetCommission){
              renderobj[uniqueKey].fleetadminFee =  parseFloat(mainArr[j].fleetCommission? mainArr[j].fleetCommission: "0").toFixed(settings.decimal);
            } else {
              renderobj[uniqueKey].fleetadminFee = (0).toFixed(settings.decimal);
            }
          }
        }
      }
      return null;
    });
    if (renderobj) {
      const arr = Object.keys(renderobj).map(i => {
        renderobj[i].myEarning = (parseFloat(renderobj[i].customerPaid) + parseFloat(renderobj[i].cancellationFee) - parseFloat(renderobj[i].driverShare)).toFixed(settings.decimal);
        renderobj[i].customerPaid = (parseFloat(renderobj[i].customerPaid) + parseFloat(renderobj[i].cancellationFee)).toFixed(settings.decimal);
        renderobj[i].rideCost = (parseFloat(renderobj[i].tripCost) - parseFloat(renderobj[i].convenienceFee)).toFixed(settings.decimal);
        renderobj[i].driverShare = parseFloat(renderobj[i].driverShare).toFixed(settings.decimal);
        return renderobj[i]
      })
      dispatch({
        type: FETCH_BOOKING__DISCOUNT_SUCCESS,
        payload: arr
      });
    }
  } else {
    dispatch({
      type: FETCH_BOOKING__DISCOUNT_FAILED,
      payload: "No data available."
    });
  }
};
